import { type MouseEvent, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

/** Sticky nav (~4.5rem) + small gap */
export const NAV_SCROLL_OFFSET_PX = 88

export function parseInternalTo(to: string): { pathname: string; hash: string } {
  const hashIndex = to.indexOf('#')
  if (hashIndex === -1) {
    const pathname = to === '' ? '/' : to
    return { pathname, hash: '' }
  }
  const pathname = to.slice(0, hashIndex) || '/'
  const hash = to.slice(hashIndex + 1)
  return { pathname, hash }
}

export function scrollToPageTarget(hash: string | undefined): void {
  const run = () => {
    if (hash) {
      const el = document.getElementById(hash)
      if (el) {
        const y =
          el.getBoundingClientRect().top +
          window.scrollY -
          NAV_SCROLL_OFFSET_PX
        window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' })
        return
      }
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
  run()
  requestAnimationFrame(run)
  setTimeout(run, 50)
  setTimeout(run, 150)
  setTimeout(run, 320)
}

type Props = {
  to: string
  className?: string
  children: ReactNode
}

/**
 * Same as Link but always scrolls to top or hash target on click, even when
 * the URL is already active (fixes repeat clicks after scrolling).
 */
export function NavLinkScroll({ to, className, children }: Props) {
  const navigate = useNavigate()

  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.button !== 0) return
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    if (to.startsWith('http://') || to.startsWith('https://')) return

    e.preventDefault()
    const { pathname, hash } = parseInternalTo(to.startsWith('/') ? to : `/${to}`)
    const pathWithHash = hash ? `${pathname}#${hash}` : pathname
    navigate(pathWithHash)
    scrollToPageTarget(hash || undefined)
  }

  return (
    <a href={to} className={className} onClick={handleClick}>
      {children}
    </a>
  )
}
